import wv, { wvReady, getCommentStatus } from '@cpc/comment-sdk-jsapi';
import { useCommentSdk, api, useStat } from '@cpc/comment-sdk-core-comment';
import * as feedsModuleController from '@cpc/lib-feeds-module-controller';
import { utils, constant } from '@cpc/comment-sdk-common';
import { onFCP } from 'web-vitals';

import { buildOptions, version } from '../package.json';

import { commentAdCallback } from './core/useCommentAd';
import { moduleCommentAdCallback } from './core/userModuleConfigAd';
import App from './App.vue';
import './assets/js/polyfilss';

// import interesting from './assets/lottie/interesting.json?url';
// import interestingLarge from './assets/lottie/interesting_large.json?url';
// import nice from './assets/lottie/nice.json?url';
// import niceLarge from './assets/lottie/nice_large.json?url';
// import amazing from './assets/lottie/amazing.json?url';
// import amazingLarge from './assets/lottie/amazing_large.json?url';
// import enjoy from './assets/lottie/enjoy.json?url';
// import enjoyLarge from './assets/lottie/enjoy_large.json?url';

/**
 * 预加载Lottie的Json动画数据，减少动画展示的延迟时间
 */
// utils.preloadLottieJsonFiles([
//   interesting,
//   nice,
//   amazing,
//   enjoy,
//   interestingLarge,
//   niceLarge,
//   amazingLarge,
//   enjoyLarge,
// ]);

const { commentSdk } = useCommentSdk({
  App,
});

const queryMapCommentId = async (commentId: string): Promise<string> => {
  let newCommentId = commentId;

  if (!/^\d+$/.test(commentId as string)) {
    newCommentId = await api
      .fetchMapCommentId({
        commentId,
      })
      .catch((error) => {
        console.error(error);

        return commentId;
      });
  }

  return newCommentId;
};

const commentAdSlotCallback
  = feedsModuleController?.getModulesConfig || window.getModulesConfig
    ? moduleCommentAdCallback
    : commentAdCallback;

// 业务默认设置
commentSdk.create({
  systemId: buildOptions.systemId,

  feature: {
    needHotComment: false,
    newCommentAddedToTop: false,
    replyDefaultDisplaySize: 1,
    replyPageSize: 10,
  },

  async beforeRender(state) {
    const { documentMeta, feature, docId, source, pageType } = state;
    let styleType = documentMeta.styleType;

    const videoType = utils.query.get('videoType');

    if (videoType) {
      // 适配os13，videoType分别为
      //  micro：小视频
      //  video：短视频
      if (videoType === 'video') {
        styleType = 2;
      } else if (videoType === 'micro') {
        styleType = 4;
      }
    } else {
      if (documentMeta.dataSource === 'yidian') {
        if (documentMeta.styleType === 'video') {
          styleType = 2;
        } else if (documentMeta.styleType === 'micro') {
          styleType = 4;
        } else if (documentMeta.styleType === 'article') {
          styleType = 1;

          // TODO(libiao): 后续版本去掉，一点短视频页面传递的为article， 此处做一次强制覆盖
          if (window?.yidian?.docinfo?.doc?.ctype === 'video_live') {
            styleType = 2;
          }
        } else {
          styleType = 0;
        }
      } else if (documentMeta.dataSource === 'oppo') {
        if (!documentMeta.styleType) {
          styleType = utils.getStyleType();

          if (styleType === 0) {
            if (window.innerWidth > 360) {
              styleType = 1;

              return;
            }

            const ratio = window.innerHeight / window.innerWidth;

            if (ratio <= 1) {
              styleType = 4;
            } else if (ratio > 1 && ratio <= 1.4) {
              styleType = 2;
            } else {
              styleType = 1;
            }
          }
        }
      }
    }

    styleType = Number(styleType);

    const commentStatus = await getCommentStatus();

    let commentId;

    if (feature.commentId) {
      commentId = await queryMapCommentId(feature.commentId);
    }

    let curDocId = docId;
    let thirdPartyId;

    const getDataSource = () => {
      // 优先区url中的sourceMedia
      let dataSource
        = utils.query.get('sourceMedia')
        || documentMeta.sourceEn
        || documentMeta.dataSource
        || source
        || '';

      switch (dataSource) {
        case 'yidianzixun':
          dataSource = 'yidian';
          break;
        case 'wangyi':
          dataSource = 'WANGYI';
          break;
        case '网易新闻':
          dataSource = 'WANGYI';
          break;
      }

      return dataSource;
    };

    const dataSource = getDataSource();

    if (utils.query.get('sourceMedia') === 'WANGYI') {
      // 三方网易源下使用三方ID作为docId
      thirdPartyId = window.location.pathname.split('/').pop().split('.').shift();
      documentMeta.commentMoreUrl = documentMeta.commentMoreUrl.replace(curDocId, thirdPartyId);
      documentMeta.replyPageUrl = documentMeta.replyPageUrl.replace(curDocId, thirdPartyId);

      curDocId = thirdPartyId;
    }

    const setIsShowTopic = () => {
      const isInCommentDetail = pageType === constant.PageType.COMMENT_DETAIL && styleType === 1;

      if (!wv.isMethodValid('SDKBrowserTopic.isShowTopic') || !isInCommentDetail) {
        return false;
      }

      return true;
    };

    const isShowTopic = setIsShowTopic();

    return {
      docId: curDocId,
      thirdPartyId,
      commentStatus,
      documentMeta: {
        styleType,
        docId: curDocId,
        dataSource,
      },
      feature: {
        commentId,
        enableTopicCard: feature.enableTopicCard && isShowTopic,
        enableSecondCommentDetail: wv.isMethodValid('launchCommentReplyPage'),
      },
    };
  },

  async validator() {
    // 置顶新闻评论或者配置__comment_sdk__为0，屏蔽评论
    return !(utils.query.get('__comment_sdk__') === '0' || utils.query.get('isTop') === '1');
  },

  // slotCallback: commentAdCallback,
  slotCallback: commentAdSlotCallback,
});

export default commentSdk;

onFCP((metric) => {
  window.generalPerfStat?.({ action: 'fcp', durationTime: metric.value });
});

commentSdk.$bus.on('afterRender', () => {
  window.commentCommonHelps?.performanceStat?.('H5_commentSdkRenderComplete');

  const durationTime = window.__TEMPLATE_COMMEMT_DATA__?.startTime
    ? Date.now() - window.__TEMPLATE_COMMEMT_DATA__.startTime
    : window.performance.now();

  window.generalPerfStat?.({ action: 'sdkRender', durationTime });
});

// vite build iife cannot export the global namespace, first mount directly
window.commentSdk = window.commentSdk || commentSdk;
